import { NumberField } from "@overmap-ai/forms"
import type { ReactNode } from "react"
import type { NumberRange } from "src/typings"

import {
	NullableNumberEqualsFilterModifier,
	NullableNumberNotEqualsFilterModifier,
	NumberGreaterThanFilterModifier,
	NumberGreaterThanOrEqualsFilterModifier,
	NumberLessThanFilterModifier,
	NumberLessThanOrEqualsFilterModifier,
	NumberRangeInFilterModifier,
	NumberRangeNotInFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { NumberFieldFilterCell } from "./NumberFieldFilterCell"

type NumberFieldValue = number | null
type NumberFilterValue = number | NumberRange | null

export class NumberFieldFilterDef extends BaseFilterDef<NumberFieldValue, NumberFilterValue> {
	readonly id: string
	readonly defaultFilterValue: NumberFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: NumberField

	constructor(field: NumberField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableNumberEqualsFilterModifier,
			isValueValid: (_value: NumberFieldValue): _value is number | null => true,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is number | null =>
				filterValue === null || typeof filterValue === "number",
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableNumberNotEqualsFilterModifier,
			isValueValid: (_value: NumberFieldValue): _value is number | null => true,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is number | null =>
				filterValue === null || typeof filterValue === "number",
		}),
		lessThan: createFilterModifierConfig({
			modifier: NumberLessThanFilterModifier,
			isValueValid: (value: NumberFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		lessThanOrEquals: createFilterModifierConfig({
			modifier: NumberLessThanOrEqualsFilterModifier,
			isValueValid: (value: NumberFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		greaterThan: createFilterModifierConfig({
			modifier: NumberGreaterThanFilterModifier,
			isValueValid: (value: NumberFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		greaterThanOrEquals: createFilterModifierConfig({
			modifier: NumberGreaterThanOrEqualsFilterModifier,
			isValueValid: (value: NumberFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		inRange: createFilterModifierConfig({
			modifier: NumberRangeInFilterModifier,
			isValueValid: (value: NumberFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is NumberRange =>
				typeof filterValue === "object" && filterValue !== null,
		}),
		notInRange: createFilterModifierConfig({
			modifier: NumberRangeNotInFilterModifier,
			isValueValid: (value: NumberFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: NumberFilterValue): filterValue is NumberRange =>
				typeof filterValue === "object" && filterValue !== null,
		}),
	}

	render(props: FilterDefRenderProps<NumberFieldFilterDef>): ReactNode {
		return <NumberFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
